import { __decorate } from "tslib";
import { AutoEncoder, field, StringDecoder } from '@common/encoding';
import { Country } from '@stamhoofd/types/Country';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { Address } from './addresses/Address.js';
/**
 * The PEPPOL schemes (ISO 6523 ICD codes) we currently support for a custom endpoint id.
 * The enum values are the actual scheme identifiers used in a PEPPOL participant id.
 */
export var PeppolScheme;
(function (PeppolScheme) {
    PeppolScheme["KBO"] = "0208";
    PeppolScheme["GLN"] = "0088";
    PeppolScheme["DUNS"] = "0060";
})(PeppolScheme || (PeppolScheme = {}));
export class PeppolSchemeHelper {
    static getLongName(scheme) {
        switch (scheme) {
            case PeppolScheme.KBO: return $t('%ZcO');
            case PeppolScheme.GLN: return $t('%Zcm');
            case PeppolScheme.DUNS: return $t('%Zcl');
        }
    }
    static getShortName(scheme) {
        switch (scheme) {
            case PeppolScheme.KBO: return $t('%ZcN');
            case PeppolScheme.GLN: return $t('%ZcS');
            case PeppolScheme.DUNS: return $t('%ZcQ');
        }
    }
}
export class PeppolEndointId extends AutoEncoder {
    schemeID;
    id;
    /**
     * The registered entity name as returned by the PEPPOL directory when the id was validated.
     * Readonly: this is set by the server during validation and cannot be set through UI patches.
     */
    entityName = null;
    get fullId() {
        return `${this.schemeID}:${this.id}`;
    }
    /**
     * A short human label for a custom endpoint id, e.g. "GLN: 5412345000013" for a known
     * scheme, or "PEPPOL ID: 0192:991825827" for any other scheme.
     */
    getShortLabel() {
        const suffix = this.entityName ? ' (' + this.entityName + ')' : '';
        const scheme = Object.values(PeppolScheme).find(s => s === this.schemeID);
        if (scheme) {
            return `${PeppolSchemeHelper.getShortName(scheme)}: ${this.id}` + suffix;
        }
        return $t('%Zci', { id: `${this.schemeID}:${this.id}` }) + suffix;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], PeppolEndointId.prototype, "schemeID", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PeppolEndointId.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 402 })
], PeppolEndointId.prototype, "entityName", void 0);
export class Company extends AutoEncoder {
    id;
    /**
     * Legal name of the organization (optional)
     */
    name = '';
    VATNumber = null;
    companyNumber = null;
    address = null;
    administrationEmail = null;
    customPeppolEndpointId;
    get peppolEndpointId() {
        if (this.customPeppolEndpointId) {
            return this.customPeppolEndpointId;
        }
        return this.peppolCompanyId;
    }
    get peppolCompanyId() {
        if (this.address?.country !== Country.Belgium) {
            // Unsupported
            return null;
        }
        const companyNumberOrVAT = this.VATNumber ?? this.companyNumber;
        if (!companyNumberOrVAT) {
            return null;
        }
        const companyNumber = (this.VATNumber ? this.VATNumber.substring(2) : companyNumberOrVAT).replace(/\D+/g, '');
        const id = Formatter.slugVATNumber(companyNumber);
        return PeppolEndointId.create({
            schemeID: '0208', // Belgium
            id,
        });
    }
    getDiffValue() {
        return this.name;
    }
    equals(other) {
        if (this.name !== other.name) {
            return false;
        }
        if (this.VATNumber !== other.VATNumber) {
            return false;
        }
        if (this.companyNumber !== other.companyNumber) {
            return false;
        }
        if (this.address && other.address) {
            if (!this.address.equals(other.address)) {
                return false;
            }
        }
        else if (!!this.address !== !!other.address) {
            return false;
        }
        if (this.administrationEmail !== other.administrationEmail) {
            return false;
        }
        return true;
    }
    isSameEntity(other) {
        if (this.VATNumber && other.VATNumber) {
            if (Formatter.slugVATNumber(this.VATNumber) === Formatter.slugVATNumber(other.VATNumber)) {
                return true;
            }
            return false;
        }
        if (this.companyNumber && other.companyNumber) {
            if (Formatter.slugVATNumber(this.companyNumber) === Formatter.slugVATNumber(other.companyNumber)) {
                if (this.address && other.address) {
                    // Ignore if different country (only for company numbers)
                    return this.address.country === other.address.country;
                }
                return true;
            }
            return false;
        }
        if (this.address && other.address) {
            if (Formatter.slug(this.name) === Formatter.slug(other.name)) {
                if (this.address.equals(other.address)) {
                    return true;
                }
            }
            return false;
        }
        else if (!!this.address === !!other.address) {
            return Formatter.slug(this.name) === Formatter.slug(other.name);
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], Company.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Company.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Company.prototype, "VATNumber", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Company.prototype, "companyNumber", void 0);
__decorate([
    field({ decoder: Address, nullable: true })
], Company.prototype, "address", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, optional: true })
], Company.prototype, "administrationEmail", void 0);
__decorate([
    field({ decoder: PeppolEndointId, nullable: true, version: 399 })
], Company.prototype, "customPeppolEndpointId", void 0);
//# sourceMappingURL=Company.js.map